/**
 * eforms.service.ts
 *
 * E-Forms specific API wrappers — covers the two endpoints that exist only
 * for the Forms tab patient modal. Survey listing, plan enrichment, and
 * SMS/email delivery all reuse existing services (surveys.service,
 * notes.service, communications.service).
 */
import { apiClient } from './apiClient';
import type { ApiResponse } from '@/types/api.types';

export interface EFormReplacementCodesResponse {
  codes: string[];
}

export interface EFormLinkResponse {
  shortUrl: string;
  fullUrl: string;
}

export interface EFormLinkPayload {
  surveyId: number;
  planId?: number | null;
  customCodes?: Record<string, string>;
}

export function getEFormReplacementCodes(
  patientId: number,
  surveyId: number,
): Promise<string[]> {
  return apiClient
    .get<ApiResponse<EFormReplacementCodesResponse>>(
      `/patients/${patientId}/eforms/replacement-codes`,
      { params: { surveyId } },
    )
    .then((r) => r.data.data?.codes ?? []);
}

export function getEFormLink(
  patientId: number,
  payload: EFormLinkPayload,
): Promise<EFormLinkResponse> {
  return apiClient
    .post<ApiResponse<EFormLinkResponse>>(
      `/patients/${patientId}/eforms/link`,
      payload,
    )
    .then((r) => r.data.data);
}

export interface EFormEmailTemplate {
  subject: string;
  body: string;
}

export interface EFormSmsTemplate {
  message: string;
}

export function getEmailTemplate(templateId: number): Promise<EFormEmailTemplate> {
  return apiClient
    .get<ApiResponse<{ subject: string; body: string; content: string }>>(
      `/email-templates/${templateId}`,
    )
    .then((r) => {
      const d = r.data.data;
      return { subject: d.subject ?? '', body: d.body ?? d.content ?? '' };
    });
}

export function getSmsTemplate(templateId: number): Promise<EFormSmsTemplate> {
  return apiClient
    .get<ApiResponse<{ message: string }>>(
      `/sms-templates/${templateId}`,
    )
    .then((r) => ({ message: r.data.data.message ?? '' }));
}

export function replaceKeywords(
  patientId: number,
  content: string,
  planId?: number | null,
): Promise<string> {
  return apiClient
    .post<ApiResponse<{ content: string }>>(
      `/patients/${patientId}/eforms/replace-keywords`,
      { content, planId: planId ?? 0 },
    )
    .then((r) => r.data.data.content);
}
